import {
  RACES,
  type Race,
  RESEARCH_DOMAINS,
  RESEARCH_TIER_COUNT,
  type ResearchDomainId,
} from "@sins2-atlas/game-data";

// The Tech Set (CONTEXT.md: Tech Set; ADR 0009): the Research Subjects and Tier unlocks a
// reader has ticked, which the page applies to every unit's stats at once. A set is always
// one the game could reach — ticking a subject ticks its prerequisite chain and the Tier
// unlocks its Tier needs in its Domain, ticking a Tier ticks the Tiers below it, and
// unticking removes the item and everything whose requirement it was. Everything here is
// pure over a ResearchIndex, so the closure rules are tested against a hand-sized index;
// the dataset-built index lives in tech-index.ts.
//
// Keys. A set is a Set of strings in compute's researchSourceKey vocabulary, with one
// widening: a Tier key carries its Race. `subject:<id>` is race-unique already (every id
// is prefixed by its race's data name), but compute's `tier:<domain>:<n>` is not —
// `tier:military:0` is a Tier Bonus source on TEC, Advent and Vasari units alike (measured
// 2026-09-05), so a race-blind key would apply one Race's tick to three Races' units. The
// key is therefore `tier:<race>:<domain>:<n>`, n being the dataset's own 0-based tier
// index (displayed I to V), and activeResearchFor projects a set onto one Race's keys in
// compute's exact vocabulary.

export type TechKey = string;
export type TechSet = ReadonlySet<TechKey>;

export const EMPTY_TECH_SET: TechSet = new Set();

/** What the closure needs to know about one Research Subject. */
export interface IndexedSubject {
  id: string;
  name: string;
  race: Race;
  domain: ResearchDomainId;
  /** 0-based, the dataset's index (displayed I to V). */
  tier: number;
  /** Prerequisite subject ids, all required. */
  prerequisites: readonly string[];
}

export interface ResearchIndex {
  subjects: ReadonlyMap<string, IndexedSubject>;
  /** Per subject id, the ids that list it as a prerequisite. */
  dependents: ReadonlyMap<string, readonly string[]>;
}

export function buildResearchIndex(subjects: Iterable<IndexedSubject>): ResearchIndex {
  const byId = new Map<string, IndexedSubject>();
  const dependents = new Map<string, string[]>();
  for (const subject of subjects) {
    byId.set(subject.id, subject);
    for (const prerequisite of subject.prerequisites) {
      const list = dependents.get(prerequisite) ?? [];
      list.push(subject.id);
      dependents.set(prerequisite, list);
    }
  }
  return { subjects: byId, dependents };
}

export function subjectKey(id: string): TechKey {
  return `subject:${id}`;
}

export function tierKey(race: Race, domain: ResearchDomainId, tier: number): TechKey {
  return `tier:${race}:${domain}:${tier}`;
}

export type ParsedKey =
  | { kind: "subject"; id: string }
  | { kind: "tier"; race: Race; domain: ResearchDomainId; tier: number };

const isRace = (value: string): value is Race => (RACES as readonly string[]).includes(value);
const isDomain = (value: string): value is ResearchDomainId =>
  (RESEARCH_DOMAINS as readonly string[]).includes(value);
/** Whether a string spells a tier index the way the grammar writes one: the canonical digits
 * of an integer from 0 to RESEARCH_TIER_COUNT - 1 and nothing else — "04", "-1", "1e0", "" and
 * the count itself are no key. The bound is the dataset's own (game-data declares it once
 * beside LEVEL_COUNT), so a patch that lengthens the ladder lengthens the grammar. */
const isTierIndex = (value: string): boolean => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n < RESEARCH_TIER_COUNT && String(n) === value;
};

/** A key back into its parts, or undefined when the string is not a key at all. Whether a
 * subject id is real is the index's business, not the grammar's. */
export function parseKey(key: TechKey): ParsedKey | undefined {
  const parts = key.split(":");
  if (parts[0] === "subject" && parts.length === 2 && parts[1] !== undefined && parts[1] !== "") {
    return { kind: "subject", id: parts[1] };
  }
  if (parts[0] === "tier" && parts.length === 4) {
    const [, race, domain, tier] = parts as [string, string, string, string];
    if (isRace(race) && isDomain(domain) && isTierIndex(tier)) {
      return { kind: "tier", race, domain, tier: Number(tier) };
    }
  }
  return undefined;
}

function addTiers(set: Set<TechKey>, race: Race, domain: ResearchDomainId, upTo: number): void {
  for (let tier = 0; tier <= upTo; tier++) set.add(tierKey(race, domain, tier));
}

function addSubject(set: Set<TechKey>, id: string, index: ResearchIndex): void {
  const key = subjectKey(id);
  if (set.has(key)) return;
  const subject = index.subjects.get(id);
  if (subject === undefined) return;
  set.add(key);
  addTiers(set, subject.race, subject.domain, subject.tier);
  for (const prerequisite of subject.prerequisites) addSubject(set, prerequisite, index);
}

/** Tick one key: the key, then everything the game requires for it. An unknown key (a
 * subject the index lacks, or a string that is no key) leaves the set as it was. */
export function tick(set: TechSet, key: TechKey, index: ResearchIndex): TechSet {
  const parsed = parseKey(key);
  if (parsed === undefined) return set;
  const next = new Set(set);
  if (parsed.kind === "subject") addSubject(next, parsed.id, index);
  else addTiers(next, parsed.race, parsed.domain, parsed.tier);
  return next;
}

function removeSubject(set: Set<TechKey>, id: string, index: ResearchIndex): void {
  const key = subjectKey(id);
  if (!set.has(key)) return;
  set.delete(key);
  for (const dependent of index.dependents.get(id) ?? []) removeSubject(set, dependent, index);
}

/** Untick one key: the key, then everything whose requirement it was — a subject's
 * dependents transitively; above an unticked Tier, the Tiers of that Domain and the
 * Domain's subjects at or above it (each taking its own dependents with it). A Tier a
 * subject pulled in survives the subject's untick: the game lets a Tier be bought alone. */
export function untick(set: TechSet, key: TechKey, index: ResearchIndex): TechSet {
  const parsed = parseKey(key);
  if (parsed === undefined || !set.has(key)) return set;
  const next = new Set(set);
  if (parsed.kind === "subject") {
    removeSubject(next, parsed.id, index);
    return next;
  }
  for (let tier = parsed.tier; tier < RESEARCH_TIER_COUNT; tier++) {
    next.delete(tierKey(parsed.race, parsed.domain, tier));
  }
  for (const subject of index.subjects.values()) {
    if (
      subject.race === parsed.race &&
      subject.domain === parsed.domain &&
      subject.tier >= parsed.tier
    ) {
      removeSubject(next, subject.id, index);
    }
  }
  return next;
}

/** The closure of several keys: each ticked in turn from the empty set. */
export function closure(keys: Iterable<TechKey>, index: ResearchIndex): TechSet {
  let set: TechSet = EMPTY_TECH_SET;
  for (const key of keys) set = tick(set, key, index);
  return set;
}

const raceOrder = (race: Race) => RACES.indexOf(race);
const domainOrder = (domain: ResearchDomainId) => RESEARCH_DOMAINS.indexOf(domain);

/** A key's canonical position: Race order, then Domain, then Tier, then a Tier before the
 * subjects of its Tier, then subject id. Equal sets serialise to equal URLs. */
function keyOrder(key: TechKey, index: ResearchIndex): [number, number, number, number, string] {
  const parsed = parseKey(key);
  if (parsed === undefined) return [Number.MAX_SAFE_INTEGER, 0, 0, 0, key];
  if (parsed.kind === "tier") {
    return [raceOrder(parsed.race), domainOrder(parsed.domain), parsed.tier, 0, ""];
  }
  const subject = index.subjects.get(parsed.id);
  if (subject === undefined) return [Number.MAX_SAFE_INTEGER, 0, 0, 1, parsed.id];
  return [raceOrder(subject.race), domainOrder(subject.domain), subject.tier, 1, subject.id];
}

function compareKeys(a: TechKey, b: TechKey, index: ResearchIndex): number {
  const left = keyOrder(a, index);
  const right = keyOrder(b, index);
  for (let i = 0; i < 4; i++) {
    const delta = (left[i] as number) - (right[i] as number);
    if (delta !== 0) return delta;
  }
  return left[4] < right[4] ? -1 : left[4] > right[4] ? 1 : 0;
}

/** The set's maximal elements, in canonical order: the ticked subjects nothing else ticked
 * requires, and the Tiers no ticked subject implies and no ticked Tier above them implies.
 * These alone rebuild the set through `closure`, so they are all the URL lists. */
export function maximalKeys(set: TechSet, index: ResearchIndex): TechKey[] {
  const implied = new Set<TechKey>();
  const topTier = new Map<string, number>();
  for (const key of set) {
    const parsed = parseKey(key);
    if (parsed === undefined) continue;
    if (parsed.kind === "tier") {
      const domain = `${parsed.race}:${parsed.domain}`;
      topTier.set(domain, Math.max(topTier.get(domain) ?? -1, parsed.tier));
      continue;
    }
    const subject = index.subjects.get(parsed.id);
    if (subject === undefined) continue;
    for (const prerequisite of subject.prerequisites) implied.add(subjectKey(prerequisite));
    for (let tier = 0; tier <= subject.tier; tier++) {
      implied.add(tierKey(subject.race, subject.domain, tier));
    }
  }
  for (const [domain, top] of topTier) {
    const [race, id] = domain.split(":") as [Race, ResearchDomainId];
    for (let tier = 0; tier < top; tier++) implied.add(tierKey(race, id, tier));
  }
  return [...set].filter((key) => !implied.has(key)).sort((a, b) => compareKeys(a, b, index));
}

/** Why each key the reader did not tick is in the set: the key of the thing that DIRECTLY
 * requires it — a subject naming it as a prerequisite or sitting in its Tier, a Tier naming
 * the Tier below it. Canonical order picks between several requirers, which prefers a Tier's
 * own subject over the Tier above it: the reader can see that subject's row for themselves.
 * Maximal keys are absent, being their own reason, and every value is itself in the set, so
 * the picker never explains a row by something it is not showing as ticked. */
export function impliers(set: TechSet, index: ResearchIndex): ReadonlyMap<TechKey, TechKey> {
  const requirers = new Map<TechKey, TechKey[]>();
  function requires(required: TechKey, requirer: TechKey): void {
    if (!set.has(required)) return;
    const list = requirers.get(required) ?? [];
    list.push(requirer);
    requirers.set(required, list);
  }
  for (const key of set) {
    const parsed = parseKey(key);
    if (parsed === undefined) continue;
    if (parsed.kind === "tier") {
      if (parsed.tier > 0) requires(tierKey(parsed.race, parsed.domain, parsed.tier - 1), key);
      continue;
    }
    const subject = index.subjects.get(parsed.id);
    if (subject === undefined) continue;
    for (const prerequisite of subject.prerequisites) requires(subjectKey(prerequisite), key);
    requires(tierKey(subject.race, subject.domain, subject.tier), key);
  }
  const maximal = new Set(maximalKeys(set, index));
  const why = new Map<TechKey, TechKey>();
  for (const [required, candidates] of requirers) {
    if (maximal.has(required)) continue;
    const [nearest] = [...candidates].sort((a, b) => compareKeys(a, b, index));
    if (nearest !== undefined) why.set(required, nearest);
  }
  return why;
}

// The URL grammar (ADR 0002): `?tech=` is a comma-separated list of the maximal elements.
// A subject is its bare id (`trader_max_hull_points_1`); a Tier is `<race>:<domain>:<n>`
// (`tec:military:2`), n being the dataset's 0-based tier index — the same index
// researchSourceKey and `research.races[race][domain].tiers[]` use, never the displayed
// numeral. A token that is neither is reported by parseTechParam, never silently dropped.

/** A key as its URL token. */
function techToken(key: TechKey): string {
  const parsed = parseKey(key);
  if (parsed === undefined) return key;
  return parsed.kind === "subject" ? parsed.id : `${parsed.race}:${parsed.domain}:${parsed.tier}`;
}

/** A URL token as its key, or undefined when the index knows no such subject and the token
 * is no Tier. */
function keyOfToken(token: string, index: ResearchIndex): TechKey | undefined {
  if (index.subjects.has(token)) return subjectKey(token);
  const asTier = parseKey(`tier:${token}`);
  return asTier?.kind === "tier" ? `tier:${token}` : undefined;
}

export interface ParsedTechParam {
  /** The closed set the recognised tokens build. */
  set: TechSet;
  /** Tokens naming nothing this index knows, deduplicated, in URL order — shown back to
   * the reader verbatim and carried on every link so a stale link survives navigation. */
  unknown: string[];
}

export function parseTechParam(value: string | undefined, index: ResearchIndex): ParsedTechParam {
  if (value === undefined || value === "") return { set: EMPTY_TECH_SET, unknown: [] };
  const keys: TechKey[] = [];
  const unknown: string[] = [];
  for (const token of new Set(value.split(","))) {
    if (token === "") continue;
    const key = keyOfToken(token, index);
    if (key === undefined) unknown.push(token);
    else keys.push(key);
  }
  return { set: closure(keys, index), unknown };
}

/** The word a whole `?tech=` slot spells for the full extent — Fully-Teched (ADR 0011).
 * It is a value, never a member: `all` is Fully-Teched only as the ENTIRE slot, and inside
 * a comma list it names no subject and no Tier, so parseTechSlot reports it like any other
 * unrecognised token rather than quietly meaning the whole set. */
export const ALL_TECH_TOKEN = "all";

export interface ParsedTechSlot extends ParsedTechParam {
  /** The slot was the single word `all`: Fully-Teched, the set's full extent. `set` is
   * empty beside it — the full extent is not a listed membership. */
  all: boolean;
}

/** One `?tech=` slot: empty for Base, the single word `all` for Fully-Teched, else today's
 * comma-separated list of maximal elements (ADR 0011 — one key, three values, which is what
 * retired `?frame=`). */
export function parseTechSlot(value: string | undefined, index: ResearchIndex): ParsedTechSlot {
  if (value === ALL_TECH_TOKEN) return { all: true, set: EMPTY_TECH_SET, unknown: [] };
  return { all: false, ...parseTechParam(value, index) };
}

/** The set as its URL value: the maximal elements' tokens, or undefined for the empty set
 * so a bare link stays canonical. Unknown tokens a link carried are appended after the
 * real ones by the caller that holds them. */
export function serializeTechParam(set: TechSet, index: ResearchIndex): string | undefined {
  const tokens = maximalKeys(set, index).map(techToken);
  return tokens.length === 0 ? undefined : tokens.join(",");
}

/** The set projected onto one Race, in compute's researchSourceKey vocabulary: subject keys
 * pass through (a subject id is race-unique, so another Race's never matches a list), and
 * this Race's Tier keys drop their race segment — every other Race's Tiers are left out,
 * which is the whole reason the Tech Set's Tier keys carry a Race. */
export function activeResearchFor(set: TechSet, race: Race): ReadonlySet<string> {
  const active = new Set<string>();
  for (const key of set) {
    const parsed = parseKey(key);
    if (parsed === undefined) continue;
    if (parsed.kind === "subject") active.add(key);
    else if (parsed.race === race) active.add(`tier:${parsed.domain}:${parsed.tier}`);
  }
  return active;
}

/** Whether the set is one the game could reach: every ticked subject has its
 * prerequisites and its Tiers ticked, and every ticked Tier has the Tiers below it. Sets
 * built by tick / untick are closed by construction; this is the check that proves it. */
export function isClosed(set: TechSet, index: ResearchIndex): boolean {
  for (const key of set) {
    const parsed = parseKey(key);
    if (parsed === undefined) return false;
    if (parsed.kind === "tier") {
      for (let tier = 0; tier < parsed.tier; tier++) {
        if (!set.has(tierKey(parsed.race, parsed.domain, tier))) return false;
      }
      continue;
    }
    const subject = index.subjects.get(parsed.id);
    if (subject === undefined) return false;
    for (let tier = 0; tier <= subject.tier; tier++) {
      if (!set.has(tierKey(subject.race, subject.domain, tier))) return false;
    }
    for (const prerequisite of subject.prerequisites) {
      if (!set.has(subjectKey(prerequisite))) return false;
    }
  }
  return true;
}
